import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { ActionBar, Button, FieldShell, Tile } from "@fawkes/ui";

// behandeling-config migration (frontend): the "Ontwormen & ontvlooien" tab — the ONTWORMEN-product
// type-specific surface, shown next to "Product" only when the product's Type = ONTWORMEN. It is the React
// replacement for the Jmix BehandelingConfigTabHost iframe (frontend-react/behandeling-config).
//
// Read: GET producten/{id}/behandeling → { productId, productNaam, version, config{23 fields}, diersoorten,
// vervolgProducten }. version === null means "no config row yet" (create-on-first-save); the config block
// carries the lazy defaults so the tab opens populated. A non-ONTWORMEN/unknown product 404s.
//
// Write: PUT producten/{id}/behandeling with { version, ...23 fields }. The config shares the
// inenting_product_config table with the Entingen tab, so the payload carries the full field set; the
// behandeling UI only surfaces the treatment-relevant subset and round-trips the rest (verplicht,
// vrijstelling*, combineerbaar, notities, geldigheids-regeling) verbatim from the loaded config. On success
// the local version re-syncs (so a follow-on save won't 409); a 409 surfaces the "gewijzigd door iemand
// anders" message. ROLE_PRODUCT_EDIT is enforced server-side; a 403 surfaces as a save error.
//
// The tab opens DIRECTLY EDITABLE (Jmix parity) with a tab-level Annuleren/Opslaan footer, owning its own
// form state so the two tabs never half-save each other. All urls are RELATIVE (the dataProvider prepends
// /api once).

interface Optie {
  id: number;
  label: string;
}

// The 23 editable config fields, faithful to BehandelingConfigResponse.BehandelingConfig (shared shape
// with the Entingen tab — the inenting_product_config row).
interface BehandelingConfig {
  diersoortId: number | null;
  vanafLeeftijdWaarde: number | null;
  vanafLeeftijdEenheid: string | null;
  totLeeftijdWaarde: number | null;
  totLeeftijdEenheid: string | null;
  toepassenAanwezig: boolean | null;
  toepassenPleeggezin: boolean | null;
  geldigheidsduurWaarde: number | null;
  geldigheidsduurEenheid: string | null;
  waarschuwingVoorafWaarde: number | null;
  waarschuwingVoorafEenheid: string | null;
  verplicht: boolean | null;
  vrijstellingToegestaan: boolean | null;
  vrijstellingReden: string | null;
  vervolgType: string | null;
  vervolgProductId: number | null;
  vervolgMomentDagen: number | null;
  combineerbaar: boolean | null;
  interneOpmerking: string | null;
  actief: boolean | null;
  notities: string | null;
  geldigVanafBasis: string | null;
  geldigTotBerekening: string | null;
}

interface BehandelingResponse {
  productId: number;
  productNaam: string;
  version: number | null;
  config: BehandelingConfig;
  diersoorten: Optie[];
  vervolgProducten: Optie[];
}

// The mutable form mirror of the treatment-relevant fields: numbers live as strings while typing.
interface BForm {
  diersoortId: string;
  vanafLeeftijdWaarde: string;
  vanafLeeftijdEenheid: string;
  totLeeftijdWaarde: string;
  totLeeftijdEenheid: string;
  toepassenAanwezig: boolean;
  toepassenPleeggezin: boolean;
  // "Herhalen elke" = geldigheidsduur waarde/eenheid (raw, not a preset — the Jmix behandeling form uses a
  // free number + unit).
  geldigheidsduurWaarde: string;
  geldigheidsduurEenheid: string;
  waarschuwingVoorafWaarde: string;
  waarschuwingVoorafEenheid: string;
  vervolgType: string;
  vervolgProductId: string;
  interneOpmerking: string;
  actief: boolean;
}

// Unit vocab faithful to the Jmix behandeling form: leeftijd = weken/maanden/jaren; herhaal =
// dagen/weken/maanden/jaren; waarschuwing = dagen/weken/maanden.
const LEEFTIJD_EENHEDEN = ["weken", "maanden", "jaren"] as const;
const HERHAAL_EENHEDEN = ["dagen", "weken", "maanden", "jaren"] as const;
const WAARSCHUWING_EENHEDEN = ["dagen", "weken", "maanden"] as const;
const VERVOLG_TYPES = ["GEEN", "ZELFDE", "ANDERE"] as const;

// The opgeslagen config may use singular unit vocab ("jaar"/"maand"/"week"/"dag") while the dropdowns work
// with the plural forms; normalise incoming values so the select shows the right option.
// Source parity: also normalise the adverb forms, so a stored "jaarlijks"/"maandelijks"/"wekelijks"/
// "dagelijks" resolves to its unit instead of silently falling back to the "maanden" default.
const NORM_INTERVAL: Record<string, string> = {
  jaar: "jaren", jaren: "jaren", jaarlijks: "jaren",
  maand: "maanden", maanden: "maanden", maandelijks: "maanden",
  week: "weken", weken: "weken", wekelijks: "weken",
  dag: "dagen", dagen: "dagen", dagelijks: "dagen",
};
const NORM_WAARSCHUWING: Record<string, string> = {
  dag: "dagen", dagen: "dagen",
  week: "weken", weken: "weken",
  maand: "maanden", maanden: "maanden",
};
const normEenheid = (map: Record<string, string>, v: string | null, fallback: string) =>
  map[(v ?? "").toLowerCase()] ?? fallback;

function toForm(c: BehandelingConfig): BForm {
  const numStr = (n: number | null) => (n == null ? "" : String(n));
  return {
    diersoortId: numStr(c.diersoortId),
    vanafLeeftijdWaarde: numStr(c.vanafLeeftijdWaarde),
    vanafLeeftijdEenheid: c.vanafLeeftijdEenheid ?? "weken",
    totLeeftijdWaarde: numStr(c.totLeeftijdWaarde),
    totLeeftijdEenheid: c.totLeeftijdEenheid ?? "maanden",
    toepassenAanwezig: c.toepassenAanwezig ?? true,
    toepassenPleeggezin: c.toepassenPleeggezin ?? true,
    geldigheidsduurWaarde: numStr(c.geldigheidsduurWaarde),
    geldigheidsduurEenheid: normEenheid(NORM_INTERVAL, c.geldigheidsduurEenheid, "maanden"),
    waarschuwingVoorafWaarde: numStr(c.waarschuwingVoorafWaarde),
    waarschuwingVoorafEenheid: normEenheid(NORM_WAARSCHUWING, c.waarschuwingVoorafEenheid, "dagen"),
    vervolgType: c.vervolgType ?? "GEEN",
    vervolgProductId: numStr(c.vervolgProductId),
    interneOpmerking: c.interneOpmerking ?? "",
    actief: c.actief ?? true,
  };
}

// A Ja/Nee toggle row (green switch + "Ja"/"Nee" text), matching the Entingen tab affordance.
function YesNoToggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  const t = useTranslate();
  return (
    <span className="entingen-toggle">
      <label className="fui-toggle product-toggle">
        <input
          type="checkbox"
          role="switch"
          className="fui-toggle__input"
          checked={checked}
          aria-label={label}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="fui-toggle__track" aria-hidden="true">
          <span className="fui-toggle__thumb" />
        </span>
      </label>
      <span className="entingen-toggle__text">
        {checked ? t("behandelingTab.toggle.ja") : t("behandelingTab.toggle.nee")}
      </span>
    </span>
  );
}

export function BehandelingTab({ id }: { id: string }) {
  const t = useTranslate();

  const { data, isLoading, isError } = useCustom<BehandelingResponse>({
    url: `producten/${id}/behandeling`,
    method: "get",
  });
  const { mutate, isLoading: saving } = useCustomMutation();

  const [form, setForm] = useState<BForm | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [notice, setNotice] = useState<"saved" | "error" | "conflict" | null>(null);

  const payload = data?.data;

  useEffect(() => {
    if (payload) {
      setVersion(payload.version ?? null);
      setForm(toForm(payload.config));
    }
  }, [payload?.productId, payload?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (isError || !payload) {
    return <p className="fawkes-error">{t("behandelingTab.loadFailed")}</p>;
  }

  const diersoorten = payload.diersoorten ?? [];
  const vervolgProducten = payload.vervolgProducten ?? [];
  const dash = "—";

  const cancelEdit = () => {
    setForm(toForm(payload.config));
    setNotice(null);
  };
  const set = <K extends keyof BForm>(key: K, value: BForm[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setNotice(null);
  };

  const eenheidLabel = (u: string) => {
    const key = `behandelingTab.eenheid.${u}`;
    const resolved = t(key);
    return resolved === key ? u : resolved;
  };
  // The source singularises at 1 ("1 maand", not "1 maanden") in the info/hint texts.
  const eenheidLabelVoor = (waarde: string | number | null | undefined, u: string) => {
    if (Number(waarde) !== 1) return eenheidLabel(u);
    const key = `behandelingTab.eenheidEnkelvoud.${u}`;
    const resolved = t(key);
    return resolved === key ? eenheidLabel(u) : resolved;
  };

  const save = () => {
    if (!form) return;
    const numOrNull = (v: string) => (v.trim() === "" ? null : Number(v));
    const idOrNull = (v: string) => (v === "" ? null : Number(v));
    const trimOrNull = (v: string) => (v.trim() === "" ? null : v);
    // Non-visible fields round-trip verbatim from the loaded config (shared inenting_product_config row).
    const base = payload.config;
    const geenVervolg = form.vervolgType === "GEEN";

    mutate(
      {
        url: `producten/${id}/behandeling`,
        method: "put",
        values: {
          version,
          // Toepassing
          diersoortId: idOrNull(form.diersoortId),
          vanafLeeftijdWaarde: numOrNull(form.vanafLeeftijdWaarde),
          vanafLeeftijdEenheid: form.vanafLeeftijdEenheid,
          totLeeftijdWaarde: numOrNull(form.totLeeftijdWaarde),
          totLeeftijdEenheid: form.totLeeftijdEenheid,
          toepassenAanwezig: form.toepassenAanwezig,
          toepassenPleeggezin: form.toepassenPleeggezin,
          // Herhaling (geldigheidsduur) — only meaningful with a follow-up; kept as-is otherwise.
          geldigheidsduurWaarde: numOrNull(form.geldigheidsduurWaarde),
          geldigheidsduurEenheid: form.geldigheidsduurEenheid,
          waarschuwingVoorafWaarde: numOrNull(form.waarschuwingVoorafWaarde),
          waarschuwingVoorafEenheid: form.waarschuwingVoorafEenheid,
          // Verplichting — not surfaced in the behandeling UI; preserved.
          verplicht: base.verplicht,
          vrijstellingToegestaan: base.vrijstellingToegestaan,
          vrijstellingReden: base.vrijstellingReden,
          // Vervolgbehandeling
          vervolgType: form.vervolgType,
          vervolgProductId: form.vervolgType === "ANDERE" ? idOrNull(form.vervolgProductId) : null,
          vervolgMomentDagen: geenVervolg ? null : base.vervolgMomentDagen,
          // Overige
          combineerbaar: base.combineerbaar,
          interneOpmerking: trimOrNull(form.interneOpmerking),
          actief: form.actief,
          // Preserved (not surfaced in the behandeling UI)
          notities: base.notities,
          geldigVanafBasis: base.geldigVanafBasis,
          geldigTotBerekening: base.geldigTotBerekening,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const updated = res.data as unknown as BehandelingResponse;
          setVersion(updated?.version ?? null);
          if (updated?.config) setForm(toForm(updated.config));
          setNotice("saved");
        },
        onError: (err) => {
          setNotice((err as { statusCode?: number }).statusCode === 409 ? "conflict" : "error");
        },
      },
    );
  };

  const noticeText =
    notice === "saved"
      ? t("behandelingTab.saved")
      : notice === "conflict"
        ? t("behandelingTab.saveConflict")
        : notice === "error"
          ? t("behandelingTab.saveError")
          : null;

  const geenVervolg = form ? form.vervolgType === "GEEN" : true;
  const herhaalTekst =
    form && form.geldigheidsduurWaarde.trim() !== ""
      ? `${form.geldigheidsduurWaarde} ${eenheidLabelVoor(form.geldigheidsduurWaarde, form.geldigheidsduurEenheid)}`
      : dash;

  return (
    <div className="entingen-tab">
      <div className="entingen-grid">
        {/* ── Toepassing ── */}
        <Tile title={t("behandelingTab.section.toepassing")}>
          <div className="entingen-section">
            <FieldShell label={t("behandelingTab.diersoort")}>
              <select
                className="fui-field__input fui-select"
                value={form?.diersoortId ?? ""}
                onChange={(e) => set("diersoortId", e.target.value)}
              >
                <option value="">{t("behandelingTab.diersoort.all")}</option>
                {diersoorten.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.label}
                  </option>
                ))}
              </select>
            </FieldShell>

            <FieldShell label={t("behandelingTab.vanafLeeftijd")}>
              <div className="entingen-waarde-eenheid">
                <input
                  type="number"
                  min="0"
                  className="fui-field__input"
                  value={form?.vanafLeeftijdWaarde ?? ""}
                  onChange={(e) => set("vanafLeeftijdWaarde", e.target.value)}
                />
                <select
                  className="fui-field__input fui-select"
                  value={form?.vanafLeeftijdEenheid ?? "weken"}
                  onChange={(e) => set("vanafLeeftijdEenheid", e.target.value)}
                >
                  {LEEFTIJD_EENHEDEN.map((u) => (
                    <option key={u} value={u}>
                      {eenheidLabel(u)}
                    </option>
                  ))}
                </select>
              </div>
            </FieldShell>

            <FieldShell label={t("behandelingTab.totLeeftijd")}>
              <div className="entingen-waarde-eenheid">
                <input
                  type="number"
                  min="0"
                  className="fui-field__input"
                  placeholder="—"
                  value={form?.totLeeftijdWaarde ?? ""}
                  onChange={(e) => set("totLeeftijdWaarde", e.target.value)}
                />
                <select
                  className="fui-field__input fui-select"
                  value={form?.totLeeftijdEenheid ?? "maanden"}
                  onChange={(e) => set("totLeeftijdEenheid", e.target.value)}
                >
                  {LEEFTIJD_EENHEDEN.map((u) => (
                    <option key={u} value={u}>
                      {eenheidLabel(u)}
                    </option>
                  ))}
                </select>
              </div>
            </FieldShell>
            <p className="fawkes-muted entingen-helper">{t("behandelingTab.totLeeftijd.helper")}</p>

            <fieldset className="entingen-fieldset">
              <legend className="entingen-legend">{t("behandelingTab.toepassenStatus")}</legend>
              <label className="entingen-check">
                <input
                  type="checkbox"
                  checked={form?.toepassenAanwezig ?? false}
                  onChange={(e) => set("toepassenAanwezig", e.target.checked)}
                />
                {t("behandelingTab.toepassenAanwezig")}
              </label>
              <label className="entingen-check">
                <input
                  type="checkbox"
                  checked={form?.toepassenPleeggezin ?? false}
                  onChange={(e) => set("toepassenPleeggezin", e.target.checked)}
                />
                {t("behandelingTab.toepassenPleeggezin")}
              </label>
            </fieldset>
          </div>
        </Tile>

        {/* ── Overige instellingen ── */}
        <Tile title={t("behandelingTab.section.overige")}>
          <div className="entingen-section">
            <FieldShell label={t("behandelingTab.interneOpmerking")} span2>
              <textarea
                className="fui-field__input"
                rows={5}
                value={form?.interneOpmerking ?? ""}
                onChange={(e) => set("interneOpmerking", e.target.value)}
              />
            </FieldShell>
            <FieldShell label={t("behandelingTab.actief")}>
              <YesNoToggle
                checked={form?.actief ?? false}
                onChange={(v) => set("actief", v)}
                label={t("behandelingTab.actief")}
              />
            </FieldShell>
          </div>
        </Tile>

        {/* ── Vervolgbehandeling (radios + inline herhaal-interval + step preview) ── */}
        <Tile title={t("behandelingTab.section.vervolg")}>
          <div className="entingen-vervolg">
            <div className="entingen-vervolg__radios" role="radiogroup">
              {VERVOLG_TYPES.map((opt) => (
                <label key={opt} className="product-radio">
                  <input
                    type="radio"
                    name="behandelingVervolgType"
                    value={opt}
                    checked={(form?.vervolgType ?? "GEEN") === opt}
                    onChange={() => set("vervolgType", opt)}
                  />
                  {t(`behandelingTab.vervolg.${opt.toLowerCase()}`)}
                </label>
              ))}
            </div>

            {geenVervolg ? (
              <p className="fawkes-muted entingen-helper">{t("behandelingTab.vervolg.geen.info")}</p>
            ) : (
              <div className="entingen-section">
                {form?.vervolgType === "ANDERE" && (
                  <FieldShell label={t("behandelingTab.vervolg.product")}>
                    <select
                      className="fui-field__input fui-select"
                      value={form?.vervolgProductId ?? ""}
                      onChange={(e) => set("vervolgProductId", e.target.value)}
                    >
                      <option value="">{t("behandelingTab.vervolg.product.select")}</option>
                      {vervolgProducten.map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </FieldShell>
                )}

                <FieldShell label={t("behandelingTab.herhalen")}>
                  <div className="entingen-waarde-eenheid">
                    <input
                      type="number"
                      min="1"
                      className="fui-field__input"
                      value={form?.geldigheidsduurWaarde ?? ""}
                      onChange={(e) => set("geldigheidsduurWaarde", e.target.value)}
                    />
                    <select
                      className="fui-field__input fui-select"
                      value={form?.geldigheidsduurEenheid ?? "maanden"}
                      onChange={(e) => set("geldigheidsduurEenheid", e.target.value)}
                    >
                      {HERHAAL_EENHEDEN.map((u) => (
                        <option key={u} value={u}>
                          {eenheidLabel(u)}
                        </option>
                      ))}
                    </select>
                  </div>
                </FieldShell>
                <p className="entingen-info entingen-info--blue">
                  {t("behandelingTab.herhalen.info", { args: [herhaalTekst] })}
                </p>

                <FieldShell label={t("behandelingTab.waarschuwingVooraf")}>
                  <div className="entingen-waarde-eenheid">
                    <input
                      type="number"
                      min="0"
                      className="fui-field__input"
                      value={form?.waarschuwingVoorafWaarde ?? ""}
                      onChange={(e) => set("waarschuwingVoorafWaarde", e.target.value)}
                    />
                    <select
                      className="fui-field__input fui-select"
                      value={form?.waarschuwingVoorafEenheid ?? "dagen"}
                      onChange={(e) => set("waarschuwingVoorafEenheid", e.target.value)}
                    >
                      {WAARSCHUWING_EENHEDEN.map((u) => (
                        <option key={u} value={u}>
                          {eenheidLabel(u)}
                        </option>
                      ))}
                    </select>
                  </div>
                </FieldShell>
                <p className="fawkes-muted entingen-helper">
                  {t("behandelingTab.waarschuwingVooraf.helper", {
                    args: [
                      `${form?.waarschuwingVoorafWaarde || "—"} ${eenheidLabelVoor(form?.waarschuwingVoorafWaarde, form?.waarschuwingVoorafEenheid ?? "dagen")}`,
                    ],
                  })}
                </p>
              </div>
            )}

            {/* Step preview — derived from vervolgType (+ herhaal-interval text). */}
            <ol className="entingen-steps">
              <li className="entingen-step entingen-step--done">
                <span className="entingen-step__dot entingen-step__dot--done" aria-hidden="true" />
                <span>
                  <strong>{t("behandelingTab.vervolg.stap1")}</strong> {t("behandelingTab.vervolg.stap1.value")}
                </span>
              </li>
              {!geenVervolg && (
                <li className="entingen-step">
                  <span className="entingen-step__dot" aria-hidden="true" />
                  <span>
                    <strong>{t("behandelingTab.vervolg.stap2")}</strong>{" "}
                    {t("behandelingTab.vervolg.stap2.value", { args: [herhaalTekst] })}
                  </span>
                </li>
              )}
            </ol>
            {/* Source's always-visible green note at the bottom of Vervolgbehandeling. */}
            <p className="entingen-info entingen-info--green">{t("behandelingTab.vervolg.softInfo")}</p>
          </div>
        </Tile>
      </div>

      <ActionBar>
        {noticeText && (
          <span className={`product-notice product-notice--${notice}`} role="status">
            {noticeText}
          </span>
        )}
        <Button variant="secondary" onClick={cancelEdit} disabled={saving}>
          {t("productEditView.cancel")}
        </Button>
        <Button variant="primary" onClick={save} disabled={saving || !form}>
          {t("productEditView.save")}
        </Button>
      </ActionBar>
    </div>
  );
}
